/**
 * Hook to handle keyboard shortcuts for the application
 * Centralizes all keyboard shortcut logic
 */

import { useEffect } from 'react'
import { eventBus } from '@/services'
import type { DrawingState, Tool } from '@/types'

interface KeyboardShortcutHandlers {
	setDrawingState: (_updates: Partial<DrawingState>) => void
	currentTool?: Tool
	/** Direct undo callback - if provided, used instead of event bus */
	onUndo?: () => void
	/** Direct redo callback - if provided, used instead of event bus */
	onRedo?: () => void
	/** Direct duplicate callback for Cmd+D */
	onDuplicate?: () => void
}

/**
 * Map of keyboard shortcuts to their actions
 */
const SHORTCUTS = {
	s: 'select',
	a: 'addPlayer',
	d: 'draw',
	e: 'erase',
	c: 'color',
	f: 'fill',
	r: 'drawing',
	h: 'hash',
	g: 'addComponent',
} as const

/**
 * Hook that sets up keyboard shortcuts for the application
 * Handles tool switching and dialog opening via keyboard
 *
 * @param handlers - Object containing state setters for tool changes
 */
export function useKeyboardShortcuts({
										 setDrawingState,
										 currentTool,
										 onUndo,
										 onRedo,
										 onDuplicate,
									 }: KeyboardShortcutHandlers): void {
	useEffect(() => {
		function handleKeyDown(event: KeyboardEvent) {
			// Ignore if user is typing in an input field
			if (event.target instanceof HTMLInputElement ||
				event.target instanceof HTMLTextAreaElement) {
				return
			}

			const key = event.key.toLowerCase()

			// Handle undo/redo with Cmd/Ctrl modifier
			if ((event.metaKey || event.ctrlKey) && key === 'z') {
				event.preventDefault()
				if (event.shiftKey) {
					// Cmd+Shift+Z = Redo
					onRedo?.()
				} else {
					// Cmd+Z = Undo
					onUndo?.()
				}
				return
			}

			// Handle duplicate with Cmd/Ctrl+D
			if ((event.metaKey || event.ctrlKey) && key === 'd') {
				event.preventDefault()
				if (currentTool === 'select' && onDuplicate) {
					onDuplicate()
				}
				return
			}

			switch (key) {
				case 's':
					event.preventDefault()
					setDrawingState({ tool: 'select' })
					eventBus.emit('dialog:closeAll')
					break
				case 'a':
					event.preventDefault()
					setDrawingState({ tool: 'addPlayer' })
					eventBus.emit('player:add', {})
					eventBus.emit('dialog:closeAll')
					break
				case 'd':
					event.preventDefault()
					eventBus.emit('dialog:openDraw')
					break
				case 'e':
					event.preventDefault()
					setDrawingState({ tool: 'erase' })
					eventBus.emit('dialog:closeAll')
					break
				case 'c':
					event.preventDefault()
					eventBus.emit('dialog:openColorPicker')
					break
				case 'f':
					event.preventDefault()
					setDrawingState({ tool: 'fill' })
					eventBus.emit('dialog:closeAll')
					break
				case 'r':
					event.preventDefault()
					eventBus.emit('dialog:openDrawing')
					break
				case 'h':
					event.preventDefault()
					eventBus.emit('dialog:openHash')
					break
				case 'g':
					event.preventDefault()
					setDrawingState({ tool: 'addComponent' })
					eventBus.emit('component:add')
					eventBus.emit('dialog:closeAll')
					break
				case 'delete':
				case 'backspace':
					// CRITICAL: Only allow deletion in select tool
					// Prevents deleting stale selection when user switches to other tools
					if (currentTool !== 'select') {
						return
					}
					event.preventDefault()
					eventBus.emit('selection:delete')
					eventBus.emit('dialog:closeAll')
					break
			}
		}

		document.addEventListener('keydown', handleKeyDown)
		return () => {
			document.removeEventListener('keydown', handleKeyDown)
		}
	}, [setDrawingState, currentTool, onUndo, onRedo, onDuplicate])
}

/**
 * Get the shortcut key for a specific tool
 * Useful for displaying shortcuts in tooltips
 */
export function getShortcutForTool(tool: Tool): string | undefined {
	const entry = Object.entries(SHORTCUTS).find(([_key, value]) => value == tool)
	return entry?.[0].toUpperCase()
}

/**
 * Get all keyboard shortcuts as a map
 * Useful for displaying a shortcuts reference
 */
export function getAllShortcuts(): Record<string, string> {
	return {
		'S': 'Select tool',
		'A': 'Add player',
		'D': 'Draw tool',
		'E': 'Erase tool',
		'C': 'Color picker',
		'F': 'Fill tool',
		'R': 'Drawing tool',
		'H': 'Hash alignment',
		'G': 'Add component',
		'Delete/⌫': 'Delete selected',
		'⌘D': 'Duplicate selection',
		'⌘Z': 'Undo',
		'⌘⇧Z': 'Redo',
	}
}
